import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Edit, Trash2, Key, UserCheck, UserX } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { TOAST_MESSAGES, CONFIRM_MESSAGES } from '../utils/toastMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

const AssistantsAdminPage = () => {
  const navigate = useNavigate();
  const [assistants, setAssistants] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    executive_ids: [],
    prefill_workspace: false
  });
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [assistantToDelete, setAssistantToDelete] = useState(null);
  const [assistantToReset, setAssistantToReset] = useState(null);
  const [diagnosticInfo, setDiagnosticInfo] = useState(null);
  const { success, error: showError } = useToast();

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const assistantsRes = await fetch(`${BACKEND_URL}/api/admin/assistants`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (assistantsRes.ok) {
        const data = await assistantsRes.json();
        setAssistants(data.assistants || []);
      }

      const execRes = await fetch(`${BACKEND_URL}/api/admin/assistants/executives/all`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (execRes.ok) {
        const data = await execRes.json();
        setExecutives(data.executives || []);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async () => {
    setError('');
    
    // Validation
    if (!formData.full_name || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.executive_ids.length > 2) {
      setError('Maximum 2 executives can be assigned per assistant');
      return;
    }

    // Use XMLHttpRequest to avoid rrweb-recorder interference with fetch
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/admin/assistants`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('x-admin-key', ADMIN_KEY);
    xhr.timeout = 15000;
    
    xhr.onload = function() {
      try {
        const data = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300 && data.ok) {
          setShowCreateModal(false);
          setFormData({ full_name: '', email: '', password: '', phone: '', executive_ids: [], prefill_workspace: false });
          
          let successMessage = 'New assistant account has been created successfully.';
          if (data.workspace_prefilled) {
            successMessage += ` Workspace pre-filled with ${data.prefill_stats?.tasks_count || 0} tasks, ${data.prefill_stats?.calendar_events_count || 0} calendar events, and ${data.prefill_stats?.travel_items_count || 0} travel items.`;
          }
          
          success('Assistant created', successMessage);
          fetchData();
        } else {
          // Handle error responses
          setDiagnosticInfo({
            status: xhr.status,
            statusText: xhr.statusText,
            response: data,
            timestamp: new Date().toISOString()
          });
          
          if (xhr.status === 401 || xhr.status === 403) {
            setError('Not authorized. Please check admin credentials.');
          } else if (xhr.status === 409 || (data.detail && data.detail.includes('already exists'))) {
            setError('Email already exists. Please use a different email address.');
          } else if (xhr.status === 400) {
            setError(data.detail || data.message || 'Invalid request. Please check all fields.');
          } else if (xhr.status === 500) {
            setError(`Server error: ${data.detail || data.message || 'Internal server error'}`);
          } else {
            setError(data.message || data.detail || 'Failed to create assistant');
          }
        }
      } catch (parseError) {
        setError('Server returned invalid response');
      }
    };
    
    xhr.onerror = function() {
      setDiagnosticInfo({
        errorType: 'NetworkError',
        errorMessage: 'Request failed',
        timestamp: new Date().toISOString()
      });
      setError('Unable to connect to server. Please check your internet connection and try again.');
    };
    
    xhr.ontimeout = function() {
      setDiagnosticInfo({
        errorType: 'TimeoutError',
        errorMessage: 'Request timed out',
        timestamp: new Date().toISOString()
      });
      setError('Request timed out. Please check your connection and try again.');
    };
    
    xhr.send(JSON.stringify(formData));
  };

  const handleEdit = async () => {
    setError('');
    
    if (!selectedAssistant) return;

    if (!formData.full_name || !formData.email) {
      setError('Full name and email are required');
      return;
    }

    if (formData.executive_ids.length > 2) {
      setError('Maximum 2 executives can be assigned per assistant');
      return;
    }

    try {
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        executive_ids: formData.executive_ids
      };

      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${selectedAssistant.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify(updateData)
      });

      const data = await res.json();
      
      if (data.ok) {
        setShowEditModal(false);
        setSelectedAssistant(null);
        setFormData({ full_name: '', email: '', password: '', phone: '', executive_ids: [] });
        success('Assistant updated', 'Profile has been updated successfully.');
        fetchData();
      } else {
        setError(data.message || 'Failed to update assistant');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleDeleteClick = (assistant) => {
    setAssistantToDelete(assistant);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!assistantToDelete) return;
    
    setIsDeleting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistantToDelete.id}`, {
        method: 'DELETE',
        headers: { 'x-admin-key': ADMIN_KEY }
      });

      if (res.ok) {
        setShowDeleteConfirm(false);
        fetchData();
        success('Assistant removed', 'Successfully removed from the system.');
      } else {
        showError('Failed to delete', 'Please try again.');
      }
    } catch (err) {
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
    setIsDeleting(false);
  };

  const handleResetPasswordClick = (assistant) => {
    setAssistantToReset(assistant);
    setShowResetConfirm(true);
  };

  const handleToggleActive = async (assistant) => {
    const newStatus = !assistant.is_active;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistant.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      const data = await res.json();
      
      if (data.ok) {
        success(
          newStatus ? 'Assistant activated' : 'Assistant deactivated',
          `Account has been ${newStatus ? 'activated' : 'deactivated'}.`
        );
        fetchData();
      } else {
        showError('Failed to update status', 'Please try again.');
      }
    } catch (err) {
      console.error('Toggle active error:', err);
      showError(TOAST_MESSAGES.NETWORK_ERROR.title, TOAST_MESSAGES.NETWORK_ERROR.message);
    }
  };

  const handleResetPassword = async () => {
    if (!assistantToReset) return;
    
    setIsResetting(true);
    try {
      // Generate random password
      const newPassword = Math.random().toString(36).slice(-10);
      
      const res = await fetch(`${BACKEND_URL}/api/admin/assistants/${assistantToReset.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (!res.ok) {
        // Handle HTTP errors with specific messages
        if (res.status === 401 || res.status === 403) {
          showError('Not authorized', 'You do not have permission to reset passwords.');
          setIsResetting(false);
          return;
        }
        if (res.status === 404) {
          showError('Assistant not found', 'The assistant account could not be found.');
          setIsResetting(false);
          return;
        }
        if (res.status === 400) {
          showError('Invalid request', 'The password reset request was invalid.');
          setIsResetting(false);
          return;
        }
        // Generic server error
        showError('Server error', 'An error occurred on the server. Please try again.');
        setIsResetting(false);
        return;
      }

      const data = await res.json();
      if (data.ok) {
        setShowResetConfirm(false);
        success(TOAST_MESSAGES.PASSWORD_RESET.title, TOAST_MESSAGES.PASSWORD_RESET.message);
        // Show new password in a second toast
        setTimeout(() => {
          success('Temporary password', `New password: ${newPassword}`, { duration: 15000 });
        }, 500);
      } else {
        showError('Failed to reset', data.message || 'Please try again.');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      // Only show "Connection issue" for actual network failures
      showError('Connection issue', 'We couldn\'t reach the server. Please check your connection.');
    } finally {
      setIsResetting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', minHeight: '100vh', background: '#F8F9FA' }}>
        <p style={{ color: '#6B7280' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '20px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ color: '#0A0A0A', fontSize: '24px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Assistant Management
            </h1>
            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
              Create and manage assistant accounts
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => window.location.href = '/admin'}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                color: '#6B7280',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Back to Admin
            </button>
            <button
              onClick={() => {
                setFormData({ full_name: '', email: '', password: '', executive_ids: [] });
                setError('');
                setShowCreateModal(true);
              }}
              style={{
                padding: '10px 16px',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              + New Assistant
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
        <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Assistant</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Employee ID</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Executives</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assistants.map((assistant, idx) => (
                <tr key={assistant.id} style={{ borderBottom: idx < assistants.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <td style={{ padding: '16px' }}>
                    <div>
                      <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 2px 0' }}>{assistant.full_name}</p>
                      <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>{assistant.email}</p>
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ color: '#374151', fontSize: '13px', fontWeight: '500', fontFamily: 'monospace' }}>{assistant.employee_id}</span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    {assistant.executives && assistant.executives.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {assistant.executives.map(exec => (
                          <span key={exec.id} style={{ color: '#374151', fontSize: '13px' }}>{exec.full_name}</span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#9CA3AF', fontSize: '13px' }}>None assigned</span>
                    )}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      padding: '4px 10px',
                      background: assistant.status === 'active' ? '#D1FAE5' : '#FEE2E2',
                      color: assistant.status === 'active' ? '#065F46' : '#991B1B',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {assistant.status}
                    </span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right' }}>
                    <button
                      onClick={() => navigate(`/admin/assistants/${assistant.id}/work`)}
                      style={{
                        padding: '6px 12px',
                        marginRight: '8px',
                        background: '#3B82F6',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                      title="View and manage assistant's work"
                    >
                      View Work
                    </button>
                    <button
                      onClick={() => {
                        setSelectedAssistant(assistant);
                        setFormData({
                          full_name: assistant.full_name,
                          email: assistant.email,
                          password: '',
                          phone: assistant.phone || '',
                          executive_ids: assistant.executives?.map(e => e.id) || []
                        });
                        setError('');
                        setShowEditModal(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        marginRight: '8px',
                        background: 'transparent',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#6B7280'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(assistant)}
                      style={{
                        padding: '6px 12px',
                        marginRight: '8px',
                        background: 'transparent',
                        border: `1px solid ${assistant.is_active ? '#FCA5A5' : '#86EFAC'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: assistant.is_active ? '#DC2626' : '#16A34A'
                      }}
                      title={assistant.is_active ? 'Deactivate account' : 'Activate account'}
                    >
                      {assistant.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleResetPasswordClick(assistant)}
                      style={{
                        padding: '6px 12px',
                        marginRight: '8px',
                        background: 'transparent',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#6B7280'
                      }}
                    >
                      Reset Password
                    </button>
                    <button
                      onClick={() => handleDeleteClick(assistant)}
                      style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        border: '1px solid #FCA5A5',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#DC2626'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {assistants.length === 0 && (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>No assistants yet. Create your first assistant to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowCreateModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #E5E7EB'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
              Create New Assistant
            </h3>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                <div style={{ marginBottom: diagnosticInfo ? '8px' : 0 }}>{error}</div>
                {diagnosticInfo && (
                  <button
                    onClick={() => {
                      alert(JSON.stringify(diagnosticInfo, null, 2));
                      console.log('=== DIAGNOSTIC INFO ===', diagnosticInfo);
                    }}
                    style={{
                      background: '#DC2626',
                      color: '#FFFFFF',
                      border: 'none',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    View Diagnostic Details
                  </button>
                )}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Full Name *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Sarah Johnson"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="sarah@company.com"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Password *</label>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Temporary password"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                  Assign Executives (max 2)
                </label>
                <div style={{ 
                  maxHeight: '150px', 
                  overflowY: 'auto', 
                  padding: '8px', 
                  background: '#F9FAFB', 
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB'
                }}>
                  {executives.map(exec => {
                    const isSelected = formData.executive_ids?.includes(exec.id);
                    return (
                      <label 
                        key={exec.id} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          padding: '8px', 
                          background: '#FFFFFF', 
                          borderRadius: '6px',
                          marginBottom: '6px',
                          cursor: 'pointer',
                          border: isSelected ? '1px solid #3B82F6' : '1px solid #E5E7EB'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const current = formData.executive_ids || [];
                            if (current.includes(exec.id)) {
                              setFormData({ ...formData, executive_ids: current.filter(id => id !== exec.id) });
                            } else {
                              if (current.length >= 2) {
                                setError('Maximum 2 executives can be assigned');
                                setTimeout(() => setError(''), 3000);
                                return;
                              }
                              setFormData({ ...formData, executive_ids: [...current, exec.id] });
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <div>
                          <p style={{ color: '#374151', fontSize: '13px', fontWeight: '500', margin: 0 }}>{exec.full_name}</p>
                          <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>{exec.title}</p>
                        </div>
                      </label>
                    );
                  })}
                  {executives.length === 0 && (
                    <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', margin: '16px 0' }}>
                      No executives available. Create executives first.
                    </p>
                  )}
                </div>
              </div>

              {/* Workspace Prefill Option */}
              {formData.executive_ids && formData.executive_ids.length > 0 && (
                <div style={{
                  background: '#F0F9FF',
                  border: '1px solid #BFDBFE',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '8px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.prefill_workspace}
                      onChange={(e) => setFormData({ ...formData, prefill_workspace: e.target.checked })}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        marginTop: '2px'
                      }}
                    />
                    <div>
                      <p style={{ color: '#1E3A8A', fontSize: '14px', fontWeight: '600', margin: '0 0 6px 0' }}>
                        ☑ Pre-fill Assistant Workspace
                      </p>
                      <p style={{ color: '#1E40AF', fontSize: '12px', margin: '0 0 8px 0', lineHeight: '1.5' }}>
                        Automatically generate a fully populated workspace with realistic tasks, calendar events, travel arrangements, and messages spanning 90 days.
                      </p>
                      <p style={{ color: '#3B82F6', fontSize: '11px', margin: 0, lineHeight: '1.4' }}>
                        This creates an active working environment that feels like ongoing operations — the assistant will immediately see a busy schedule, upcoming travel, and meaningful tasks to complete.
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Create Assistant
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedAssistant && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowEditModal(false)}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #E5E7EB'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
              Edit Assistant Profile
            </h3>

            {error && (
              <div style={{
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#991B1B',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Full Name *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>Phone (optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>
                  Assign Executives (max 2)
                </label>
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  padding: '8px', 
                  background: '#F9FAFB', 
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB'
                }}>
                  {executives.map(exec => {
                    const isSelected = formData.executive_ids?.includes(exec.id);
                    return (
                      <label 
                        key={exec.id} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          padding: '8px', 
                          background: '#FFFFFF', 
                          borderRadius: '6px',
                          marginBottom: '6px',
                          cursor: 'pointer',
                          border: isSelected ? '1px solid #3B82F6' : '1px solid #E5E7EB'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const current = formData.executive_ids || [];
                            if (current.includes(exec.id)) {
                              setFormData({ ...formData, executive_ids: current.filter(id => id !== exec.id) });
                            } else {
                              if (current.length >= 2) {
                                setError('Maximum 2 executives can be assigned');
                                setTimeout(() => setError(''), 3000);
                                return;
                              }
                              setFormData({ ...formData, executive_ids: [...current, exec.id] });
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <div>
                          <p style={{ color: '#374151', fontSize: '13px', fontWeight: '500', margin: 0 }}>{exec.full_name}</p>
                          <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>{exec.title}</p>
                        </div>
                      </label>
                    );
                  })}
                  {executives.length === 0 && (
                    <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', margin: '16px 0' }}>
                      No executives available. Create executives first.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedAssistant(null);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        {...CONFIRM_MESSAGES.DELETE_ASSISTANT}
        isLoading={isDeleting}
      />

      {/* Reset Password Confirmation Modal */}
      <ConfirmModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleResetPassword}
        {...CONFIRM_MESSAGES.RESET_PASSWORD}
        isLoading={isResetting}
      />
    </div>
  );
};

export default AssistantsAdminPage;
